import { merge } from 'lodash';

/**
 * Recursive function to create a new style object based upon new/current/old styles.
 * @param {object} newStyle - Collection of new styles, not yet applied.
 * @param {object} [currentStyle={}] - Collection of current styles.
 * @param {object} [oldStyle={}] - Collection of old styles, previously applied.
 * @returns {object} - Final collection of styles.
 */
export function buildSoftStyles(newStyle, currentStyle = {}, oldStyle = {}) {
	// Check if the newStyle is an object and not null
	if (typeof newStyle === 'object' && newStyle !== null) {
		// Initialize updatedStyle as an array if newStyle is an array, otherwise as an object
		const updatedStyle = Array.isArray(newStyle) ? [] : {};

		// Iterate over keys in newStyle
		for (const key in newStyle) {
			// Ensure the key is an own property and not inherited
			if (Object.prototype.hasOwnProperty.call(newStyle, key)) {
				// Recursively call buildSoftStyles for each sub-property
				// This allows the function to handle nested style objects
				updatedStyle[key] = buildSoftStyles(
					newStyle[key],
					currentStyle[key],
					oldStyle[key]
				);
			}
		}

		// Return the aggregated style object
		return updatedStyle;
	}

	// If newStyle is not an object (i.e., a base case for recursion):
	// Check if the current style is the same as the old style
	if (currentStyle === oldStyle) {
		// If current style is the same as old style, return the new style
		return newStyle;
	}

	// If current style differs from old style, keep the current style
	return currentStyle;
}

/**
 * Utility function to update component styles as part of panel style updates.
 * @param {object} collection - Collection of components to update.
 * @param {boolean} doSoftUpdate - Flag that enables/disables check for if individual components have
 *                                 been altered from oldComponentStyles.
 * @param {Function} getOldStyles - Getter for old component styles.
 * @param {Function} getNewStyles - Getter for new component styles.
 * @param {Function} getCurrentStylesParent - Getter for parent of current styles. This is necessary to
 *                                            update the proper collection after the function has executed.
 * @param {object[]} subUpdates - Collection of updates to run on sub-components if a test is passed.
 */
export function updateComponentStyles(collection, doSoftUpdate, getOldStyles, getNewStyles, getCurrentStylesParent, subUpdates = []) {
	for (const key in collection) {
		if (Object.prototype.hasOwnProperty.call(collection, key)) {
			const component = collection[key];
			const currentComponentStylesParent = getCurrentStylesParent(component);
			const oldComponentStyles = getOldStyles(component);
			const newComponentStyles = getNewStyles(component);
			const localComponentStyles = doSoftUpdate
				? buildSoftStyles(
					newComponentStyles,
					currentComponentStylesParent.style,
					oldComponentStyles
				)
				: newComponentStyles;

			// Update the current component styles
			currentComponentStylesParent.style = {
				...oldComponentStyles,
				...newComponentStyles,
				...localComponentStyles
			};

			// Update styles of sub-components (collections that live in the current component)
			subUpdates.forEach(sub => {
				if (sub.test(component)) sub.updater(component);
			});
		}
	}
}

export default {
	buildSoftStyles,
	updateComponentStyles
};
